import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    DollarSign, TrendingUp, TrendingDown, Plane, Users,
    BarChart3, Loader2, ArrowUpRight, ArrowDownRight,
    Target, Fuel, Route
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell, RadarChart, Radar,
    PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const periods = [
    { key: 'week', label: '7 dias' },
    { key: 'month', label: '30 dias' },
    { key: 'quarter', label: '90 dias' },
    { key: 'year', label: '1 ano' },
];
const COLORS = ['#0ea5e9', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#f97316', '#ec4899'];

function KPICard({ icon: Icon, label, value, subvalue, trend, color = 'primary' }) {
    const colorMap = {
        primary: 'bg-primary/10 text-primary',
        emerald: 'bg-emerald-500/10 text-emerald-500',
        amber: 'bg-amber-500/10 text-amber-500',
        rose: 'bg-rose-500/10 text-rose-500',
        violet: 'bg-violet-500/10 text-violet-500',
        sky: 'bg-sky-500/10 text-sky-500',
    };
    return (
        <Card className="card-aviation">
            <CardContent className="p-4">
                <div className="flex items-start justify-between">
                    <div className={`w-10 h-10 rounded-lg ${colorMap[color]} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                    </div>
                    {trend !== undefined && (
                        <div className={`flex items-center gap-1 text-xs font-bold ${trend >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                            {trend >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                            {Math.abs(trend)}%
                        </div>
                    )}
                </div>
                <div className="mt-3">
                    <p className="text-2xl font-bold font-mono">{value}</p>
                    {subvalue && <p className="text-xs text-muted-foreground mt-0.5">{subvalue}</p>}
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-bold uppercase tracking-wider">{label}</p>
            </CardContent>
        </Card>
    );
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-card border border-border/50 rounded-lg p-3 shadow-lg text-xs">
            <p className="font-bold mb-1.5">{label}</p>
            {payload.map((p, i) => (
                <div key={i} className="flex items-center gap-2 py-0.5">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
                    <span className="text-muted-foreground">{p.name}:</span>
                    <span className="font-mono font-bold">{typeof p.value === 'number' ? (p.name.includes('$') || p.name.includes('Revenue') || p.name.includes('Costo') ? `$${p.value.toLocaleString()}` : p.value.toLocaleString()) : p.value}</span>
                </div>
            ))}
        </div>
    );
}

export default function RevenuePage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('year');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/analytics/revenue?period=${period}`);
            setData(res.data);
        } catch {
            toast.error('Error al cargar datos de revenue');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!data) return null;

    const { summary: s, routes } = data;

    // Chart data
    const revenueBarData = routes.map(r => ({
        route: r.route,
        Revenue: Math.round(r.total_revenue),
        Costo: Math.round(r.total_cost),
    }));

    const loadFactorData = routes.map(r => ({
        route: r.route,
        'Load Factor': r.load_factor,
    }));

    const pieData = routes.map((r, i) => ({
        name: r.route,
        value: Math.round(r.total_revenue),
        color: COLORS[i % COLORS.length],
    }));

    const radarData = routes.slice(0, 6).map(r => ({
        route: r.route,
        'Load Factor': r.load_factor,
        Margen: Math.max(r.margin, 0),
        Yield: Math.min(r.yield_rpk * 10, 100),
    }));

    return (
        <div className="space-y-6" data-testid="revenue-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="revenue-title">
                        Revenue & Yield
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Analisis de rentabilidad por ruta</p>
                </div>
                <div className="flex items-center gap-2" data-testid="period-selector">
                    {periods.map(p => (
                        <Button
                            key={p.key}
                            variant={period === p.key ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPeriod(p.key)}
                            className="text-xs font-bold uppercase tracking-wider"
                            data-testid={`period-${p.key}`}
                        >
                            {p.label}
                        </Button>
                    ))}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
                <KPICard icon={DollarSign} label="Revenue Total" value={`$${s.total_revenue.toLocaleString()}`} subvalue={`${s.total_flights} vuelos`} color="emerald" />
                <KPICard icon={Target} label="Load Factor" value={`${s.overall_load_factor}%`} subvalue={`${s.total_pax.toLocaleString()} pax / ${s.total_capacity.toLocaleString()} cap`} color="sky" />
                <KPICard icon={TrendingUp} label="Margen Operativo" value={`${s.overall_margin}%`} subvalue={`Profit: $${s.total_profit.toLocaleString()}`} color={s.overall_margin >= 0 ? 'emerald' : 'rose'} />
                <KPICard icon={BarChart3} label="Yield (RPK)" value={`${s.overall_yield}c`} subvalue={`CASK: ${s.overall_cask}c | RASK: ${s.overall_rask}c`} color="violet" />
            </div>

            {/* Revenue vs Cost Chart + Revenue Distribution */}
            <div className="grid lg:grid-cols-3 gap-6">
                <Card className="card-aviation lg:col-span-2" data-testid="revenue-cost-chart">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-primary" /> Revenue vs Costo por Ruta
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={revenueBarData} barGap={4}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                                <XAxis dataKey="route" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Costo" fill="#ef4444" radius={[4, 4, 0, 0]} opacity={0.7} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Card className="card-aviation" data-testid="revenue-distribution-chart">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                            <Route className="w-4 h-4 text-primary" /> Distribucion
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={3} strokeWidth={0}>
                                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 10 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Load Factor + Radar */}
            <div className="grid lg:grid-cols-2 gap-6">
                <Card className="card-aviation" data-testid="load-factor-chart">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                            <Users className="w-4 h-4 text-primary" /> Load Factor por Ruta
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={loadFactorData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `${v}%`} />
                                <YAxis dataKey="route" type="category" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={90} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="Load Factor" radius={[0, 4, 4, 0]}>
                                    {loadFactorData.map((entry, i) => (
                                        <Cell key={i} fill={entry['Load Factor'] >= 70 ? '#10b981' : entry['Load Factor'] >= 50 ? '#f59e0b' : '#ef4444'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Card className="card-aviation" data-testid="radar-chart">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                            <Target className="w-4 h-4 text-primary" /> Perfil de Rendimiento
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <RadarChart data={radarData}>
                                <PolarGrid stroke="hsl(var(--border))" opacity={0.3} />
                                <PolarAngleAxis dataKey="route" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                                <PolarRadiusAxis tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" />
                                <Radar name="Load Factor" dataKey="Load Factor" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.15} />
                                <Radar name="Margen" dataKey="Margen" stroke="#10b981" fill="#10b981" fillOpacity={0.15} />
                                <Legend wrapperStyle={{ fontSize: 10 }} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Route Table */}
            <Card className="card-aviation" data-testid="routes-table-card">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                        <Plane className="w-4 h-4 text-primary" /> Detalle por Ruta
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="routes-table">
                            <thead>
                                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border/50">
                                    <th className="text-left py-3 px-3 font-bold">Ruta</th>
                                    <th className="text-center py-3 px-2 font-bold">Vuelos</th>
                                    <th className="text-center py-3 px-2 font-bold">Pax</th>
                                    <th className="text-center py-3 px-2 font-bold">LF%</th>
                                    <th className="text-right py-3 px-2 font-bold">Revenue</th>
                                    <th className="text-right py-3 px-2 font-bold">Costo</th>
                                    <th className="text-right py-3 px-2 font-bold">Profit</th>
                                    <th className="text-center py-3 px-2 font-bold">Margen</th>
                                    <th className="text-center py-3 px-2 font-bold">Yield</th>
                                    <th className="text-center py-3 px-2 font-bold">CASK</th>
                                </tr>
                            </thead>
                            <tbody>
                                {routes.map((r, i) => (
                                    <tr key={r.route} className="border-b border-border/30 hover:bg-muted/30 transition-colors" data-testid={`route-row-${i}`}>
                                        <td className="py-3 px-3">
                                            <div className="flex items-center gap-2">
                                                <Plane className="w-3.5 h-3.5 text-primary" />
                                                <span className="font-bold font-mono text-xs">{r.route}</span>
                                                <span className="text-[10px] text-muted-foreground">{r.distance_nm}nm</span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-2 text-center font-mono">{r.flights}</td>
                                        <td className="py-3 px-2 text-center font-mono">{r.total_pax}</td>
                                        <td className="py-3 px-2 text-center">
                                            <Badge className={r.load_factor >= 70 ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : r.load_factor >= 50 ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-rose-500/10 text-rose-500 border-rose-500/20'}>
                                                {r.load_factor}%
                                            </Badge>
                                        </td>
                                        <td className="py-3 px-2 text-right font-mono font-bold text-emerald-500">${r.total_revenue.toLocaleString()}</td>
                                        <td className="py-3 px-2 text-right font-mono text-rose-400">${r.total_cost.toLocaleString()}</td>
                                        <td className="py-3 px-2 text-right font-mono font-bold">
                                            <span className={r.profit >= 0 ? 'text-emerald-500' : 'text-rose-500'}>
                                                ${r.profit.toLocaleString()}
                                            </span>
                                        </td>
                                        <td className="py-3 px-2 text-center">
                                            <span className={`font-mono text-xs font-bold ${r.margin >= 30 ? 'text-emerald-500' : r.margin >= 0 ? 'text-amber-500' : 'text-rose-500'}`}>
                                                {r.margin}%
                                            </span>
                                        </td>
                                        <td className="py-3 px-2 text-center font-mono text-xs">{r.yield_rpk}c</td>
                                        <td className="py-3 px-2 text-center font-mono text-xs">{r.cask}c</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 p-3 rounded-lg bg-muted/30 text-xs text-muted-foreground grid sm:grid-cols-3 gap-2">
                        <div><strong>ASK:</strong> Available Seat Kilometers ({s.total_ask.toLocaleString()})</div>
                        <div><strong>RPK:</strong> Revenue Passenger Kilometers ({s.total_rpk.toLocaleString()})</div>
                        <div><strong>Yield:</strong> Revenue por RPK en centavos ({s.overall_yield}c)</div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
